// src/data/repeatMatches.ts
// -------------------------
// Repository for the possible-repeat flag (db-210). The matching RULES live only in the
// database function request_repeat_matches() — this file just calls it. The function is
// SECURITY INVOKER, so the caller's own RLS decides what it can see.
//
// BEST-EFFORT by design: this NEVER throws. A failed lookup means "no flag shown"; it must
// never block opening or confirming a request.

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type RepeatMatchRow =
  Database["public"]["Functions"]["request_repeat_matches"]["Returns"][number];

export interface RepeatCheckSettings {
  /** Look-back window in months (portal_config; default 12). */
  months?: number | null;
  /** Item types to check (fee types + "Device"). null/undefined = all types. */
  types?: string[] | null;
}

export type RepeatMatchesResult = { rows: RepeatMatchRow[]; error: { message: string } | null };

export async function getRepeatMatches(
  requestId: number | string | null | undefined,
  settings: RepeatCheckSettings = {}
): Promise<RepeatMatchesResult> {
  const id = Number(requestId);
  if (!Number.isFinite(id) || id <= 0) return { rows: [], error: null };
  const c = getTypedClient();
  if (!c) return { rows: [], error: null };
  const args: { p_request_id: number; p_months?: number; p_types?: string[] } = {
    p_request_id: id,
  };
  if (settings.months != null && Number.isFinite(Number(settings.months))) {
    args.p_months = Math.max(1, Math.trunc(Number(settings.months)));
  }
  if (Array.isArray(settings.types)) args.p_types = settings.types;
  try {
    const { data, error } = await c.rpc("request_repeat_matches", args);
    if (error) {
      logger.warn("request_repeat_matches failed:", error.message);
      return { rows: [], error };
    }
    return { rows: Array.isArray(data) ? data : [], error: null };
  } catch (e) {
    logger.warn("request_repeat_matches error:", e);
    return { rows: [], error: { message: String(e) } };
  }
}

// ── Phase B (db-211): the summary for a whole LIST of requests ─────────────────────────────
export type RepeatFlagRow =
  Database["public"]["Functions"]["request_repeat_flags"]["Returns"][number];

export type RepeatFlagsResult = { rows: RepeatFlagRow[]; error: { message: string } | null };

/**
 * Possible-repeat summary for many requests in ONE call (request_repeat_flags, db-211),
 * for the Responders Task List and All Requests. Only flagged requests come back.
 * Never throws: on any error the list simply shows no chips.
 */
export async function getRepeatFlags(
  requestIds: (number | string | null | undefined)[] | null | undefined,
  settings: RepeatCheckSettings = {}
): Promise<RepeatFlagsResult> {
  const ids = Array.from(
    new Set((requestIds || []).map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0))
  );
  if (ids.length === 0) return { rows: [], error: null };
  const c = getTypedClient();
  if (!c) return { rows: [], error: null };
  const args: { p_request_ids: number[]; p_months?: number; p_types?: string[] } = {
    p_request_ids: ids,
  };
  if (settings.months != null && Number.isFinite(Number(settings.months))) {
    args.p_months = Math.max(1, Math.trunc(Number(settings.months)));
  }
  if (Array.isArray(settings.types)) args.p_types = settings.types;
  try {
    const { data, error } = await c.rpc("request_repeat_flags", args);
    if (error) {
      logger.warn("request_repeat_flags failed:", error.message);
      return { rows: [], error };
    }
    return { rows: Array.isArray(data) ? data : [], error: null };
  } catch (e) {
    logger.warn("request_repeat_flags error:", e);
    return { rows: [], error: { message: String(e) } };
  }
}
